/**
 * Location Scope Utilities (Frontend)
 * 
 * Utilities to extract accessible provinces and cities from admin assignments.
 */

import type { User } from "@/services/authService";
import { locationService } from "@/services/locationService";
import { countsAsEffectiveNasionalAssignment } from "@/utils/privileges";

export interface LocationScope {
  provinces: string[];
  cities: string[];
  level: "nasional" | "province" | "city" | null;
}

/**
 * Get location scope from user's admin assignments
 * @param user - User object with adminAssignments
 * @returns LocationScope with accessible provinces and cities
 */
export async function getUserLocationScope(user: User | null | undefined): Promise<LocationScope> {
  if (!user || !user.adminAssignments || user.adminAssignments.length === 0) {
    return {
      provinces: [],
      cities: [],
      level: null,
    };
  }

  const provincesSet = new Set<string>();
  const citiesSet = new Set<string>();
  let maxLevel: "nasional" | "province" | "city" | null = null;

  // Collect provinces and cities from all active admin assignments
  user.adminAssignments.forEach((assignment) => {
    if (!assignment.isActive) return;

    const effectiveNasional =
      assignment.level === "nasional" && countsAsEffectiveNasionalAssignment(user, assignment);

    // Track the highest level
    if (effectiveNasional) {
      maxLevel = "nasional";
    } else if (assignment.level === "province" && maxLevel !== "nasional") {
      maxLevel = "province";
    } else if (assignment.level === "city" && maxLevel === null) {
      maxLevel = "city";
    }

    // If nasional level, no location restrictions
    if (assignment.level === "nasional") {
      return; // Skip adding specific locations for nasional (effective or not)
    }

    // Add provinces
    if (assignment.provinces && Array.isArray(assignment.provinces)) {
      assignment.provinces.forEach((province: string) => {
        if (province && province.trim()) {
          provincesSet.add(province.trim());
        }
      });
    }

    // Add cities
    if (assignment.cities && Array.isArray(assignment.cities)) {
      assignment.cities.forEach((city: string) => {
        if (city && city.trim()) {
          citiesSet.add(city.trim());
        }
      });
    }
  });

  // If nasional level, return empty arrays (no restrictions)
  if (maxLevel === "nasional") {
    return {
      provinces: [],
      cities: [],
      level: "nasional",
    };
  }

  // If province level, fetch all cities under the assigned provinces
  if (maxLevel === "province" && provincesSet.size > 0) {
    const allCitiesSet = new Set<string>();
    
    // Fetch all cities for each assigned province
    for (const province of provincesSet) {
      try {
        const cities = await locationService.fetchCitiesByProvince(province);
        cities.forEach((city) => {
          if (city && city.trim()) {
            allCitiesSet.add(city.trim());
          }
        });
      } catch (error) {
        console.error(`Error fetching cities for province ${province}:`, error);
      }
    }
    
    return {
      provinces: Array.from(provincesSet),
      cities: Array.from(allCitiesSet),
      level: maxLevel,
    };
  }

  return {
    provinces: Array.from(provincesSet),
    cities: Array.from(citiesSet),
    level: maxLevel,
  };
}

